import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { QAlert, QBox, QButton, QFlex, useCurrentUser, useToastProvider } from '@qualio/ui-components';

import eventsApi from '../../api/events.api';
import { useEventTemplateFormValues } from '../../hooks';
import { EventTemplateFieldAttributeNames, EventTemplateFields, EventTemplateFormValues } from '../../types';
import { compare } from '../../utils/arrayUtils';
import { camelOrSnakeToTitle } from '../../utils/textUtils';

type UpdateTemplateFieldsAlertProps = {
  ownerId: number;
  eventTemplateId: number;
  existingTemplateFields?: EventTemplateFields;
  eventId: number;
  updateEventTemplateFields: (templateFields: EventTemplateFields) => void;
};

const UpdateTemplateFieldsAlert: React.FC<UpdateTemplateFieldsAlertProps> = ({
  eventTemplateId,
  existingTemplateFields,
  eventId,
  updateEventTemplateFields,
  ownerId,
}) => {
  const { t } = useTranslation();
  const { companyId, userId } = useCurrentUser();
  const isOwner = ownerId === userId;
  const templateResult = useEventTemplateFormValues(companyId, eventTemplateId);
  const { showToast } = useToastProvider();

  const [addedFields, setAddedFields] = useState<string[]>([]);
  const [removedFields, setRemovedFields] = useState<string[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [notRequiredFields, setNotRequiredFields] = useState<string[]>([]);
  const notRequiredLabel = t('events.messages.notRequired').replace(/:\s*$/, '');
  const requiredLabel = t('common.statuses.required');

  const fieldsFromTemplateKeys = Object.keys(templateResult.data?.fields ?? {});

  const hasTemplateFields = () => {
    return (
      (existingTemplateFields && existingTemplateFields.length !== 0) ||
      (templateResult.data?.fields && fieldsFromTemplateKeys.length !== 0)
    );
  };

  const hasTemplateFieldChanges = () => {
    return (
      addedFields.length > 0 || removedFields.length > 0 || requiredFields.length > 0 || notRequiredFields.length > 0
    );
  };

  const templateFieldRef = useRef((templateResult.data as EventTemplateFormValues)?.fields);

  // decide if we should actually update the reference for useEffectUpdate
  if (!compare(Object.keys(templateFieldRef.current ?? {}), fieldsFromTemplateKeys)) {
    templateFieldRef.current = (templateResult.data as EventTemplateFormValues)?.fields;
  }

  const fieldsFromTemplate = templateFieldRef.current;

  useEffect(() => {
    if (templateResult.isLoading) {
      return;
    }

    const keys = Object.keys(EventTemplateFieldAttributeNames.enum);

    const added: string[] = [];
    const removed: string[] = [];
    const required: string[] = [];
    const notRequired: string[] = [];

    keys.forEach((key) => {
      const readableKey = camelOrSnakeToTitle(key);
      const normalizedReadableKey = readableKey ? readableKey.charAt(0).toUpperCase() + readableKey.slice(1) : '';
      const existingHasField = existingTemplateFields?.find((existingField) => existingField.attribute_name === key);
      const newHasField = fieldsFromTemplate?.[key];

      if (Boolean(existingHasField) !== Boolean(newHasField)) {
        if (existingHasField) {
          removed.push(normalizedReadableKey);
        } else {
          const fieldTypeLabel = newHasField?.mandatory ? requiredLabel : notRequiredLabel;
          added.push(`${normalizedReadableKey} (${fieldTypeLabel})`);
        }
      } else if (Boolean(existingHasField?.mandatory) !== Boolean(newHasField?.mandatory)) {
        if (existingHasField?.mandatory) {
          notRequired.push(normalizedReadableKey);
        } else {
          required.push(normalizedReadableKey);
        }
      }
    });

    if (removed.length > 0) {
      setRemovedFields(removed);
    }
    if (added.length > 0) {
      setAddedFields(added);
    }
    if (notRequired.length > 0) {
      setNotRequiredFields(notRequired);
    }
    if (required.length > 0) {
      setRequiredFields(required);
    }
  }, [existingTemplateFields, templateResult.isLoading, fieldsFromTemplate, notRequiredLabel, requiredLabel]);

  const sync = useCallback(async () => {
    try {
      const qualityEventSyncResponse = await eventsApi.syncTemplateFields(companyId, eventId, eventTemplateId);
      updateEventTemplateFields(qualityEventSyncResponse.template_fields);

      showToast({
        title: t('common.notifications.eventUpdated'),
        description: t('common.notifications.eventUpdatedToLatestTemplate'),
        status: 'success',
        'data-cy': 'qe-event-update-success-toast',
      });
    } catch {
      showToast({
        title: t('common.notifications.eventNotUpdated'),
        description: t('common.notifications.eventNotUpdatedToLatestTemplate'),
        status: 'error',
      });
    }
  }, [companyId, eventId, eventTemplateId, updateEventTemplateFields, showToast, t]);

  if (!hasTemplateFields() || !hasTemplateFieldChanges()) {
    return null;
  }

  const alertDescription = (
    <QBox marginLeft={5}>
      <QFlex justify="space-between">
        <ul>
          {(removedFields.length && (
            <li>
              {t('events.messages.removedFromProperties')} {removedFields.join(', ')}
            </li>
          )) ||
            ''}
          {(addedFields.length && (
            <li>
              {t('events.messages.addedToProperties')} {addedFields.join(', ')}
            </li>
          )) ||
            ''}
          {(requiredFields.length && (
            <li>
              {t('events.messages.requiredColon')} {requiredFields.join(', ')}
            </li>
          )) ||
            ''}
          {(notRequiredFields.length && (
            <li>
              {t('events.messages.notRequired')} {notRequiredFields.join(', ')}
            </li>
          )) ||
            ''}
        </ul>
        {isOwner ? (
          <QFlex align="center">
            <QButton aria-label={t('events.actions.updateEvent')} size="sm" variant="outline" onClick={sync}>
              {t('events.actions.updateEvent')}
            </QButton>
          </QFlex>
        ) : null}
      </QFlex>
    </QBox>
  );

  return (
    <QAlert
      title={t('templates.messages.updatesHaveBeenMadeToThisTemplate')}
      status="warning"
      description={alertDescription}
    ></QAlert>
  );
};

export default UpdateTemplateFieldsAlert;
